import { useLocalStorageState } from 'ahooks';
import { useCallback } from 'react';
import { NewFeatureKeys } from '@/app/constants';

/**
 * 新功能的过期日期配置
 * 格式：功能键名 -> 过期日期（ISO 字符串）
 * 如果功能已过期，即使未被点击也不会显示新功能提示
 */
const FEATURE_EXPIRATION_DATES: Partial<Record<NewFeatureKeys, string>> = {
  [NewFeatureKeys.QUICK_EDIT]: '2026-02-26T00:00:00Z',
  [NewFeatureKeys.QUICK_EDIT_ADD_IMAGE]: '2026-02-26T00:00:00Z',
};

/**
 * 新功能状态管理 Hook
 * 用于统一管理各种新功能的展示状态和点击记录
 */
export const useNewFeature = () => {
  const [newFeatureClicked, setNewFeatureClicked] = useLocalStorageState<NewFeatureKeys[]>('NEW_FEATURE_CLICKED', {
    defaultValue: [],
    listenStorageChange: true,
  });

  /**
   * 检查某个功能是否为新功能（未被点击过且未过期）
   * @param featureKey 功能键名
   * @returns 是否为新功能
   */
  const isNewFeature = useCallback(
    (featureKey: NewFeatureKeys): boolean => {
      // 如果已被点击过，不是新功能
      if ((newFeatureClicked ?? []).includes(featureKey)) {
        return false;
      }

      // 检查是否过期
      const expirationDate = FEATURE_EXPIRATION_DATES[featureKey];
      if (expirationDate) {
        const now = new Date();
        const expireDate = new Date(expirationDate);
        // 如果当前时间已超过过期日期，不显示新功能提示
        if (now > expireDate) {
          return false;
        }
      }

      return true;
    },
    [newFeatureClicked],
  );

  /**
   * 标记某个功能为已点击
   * @param featureKey 功能键名
   */
  const markAsClicked = useCallback(
    (featureKey: NewFeatureKeys) => {
      setNewFeatureClicked((prev) => {
        const current = prev || [];
        if (!current.includes(featureKey)) {
          return [...current, featureKey];
        }
        return current;
      });
    },
    [setNewFeatureClicked],
  );

  return {
    // 状态查询
    isNewFeature,
    // 状态更新
    markAsClicked,
  };
};
